/**
 * Format-Helpers — Locale-bewusst (DE-Default), konsistent in der ganzen App.
 * Niemals direkt `toLocaleString` in Components rufen — sonst entstehen unterschiedliche
 * Formatierungen je nach Browser/Aufrufstelle.
 */

const DE = 'de-DE';

export function formatCurrency(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat(DE, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Wie `formatCurrency`, aber immer mit zwei Nachkommastellen — für Belege und Preislisten.
 *
 * <p>`formatCurrency` lässt die Cents weg, wenn sie null sind. Auf einer Kachel ist das
 * angenehm, auf einer Rechnungsliste falsch: „110 €" neben „8,50 €" liest sich wie zwei
 * verschiedene Genauigkeiten, und auf einem Beleg gehört der Cent hin.</p>
 */
export function formatMoney(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat(DE, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Nächtlicher Preis im Kalenderraster — volle Euro ohne „,00", Cents nur wenn vorhanden.
 *
 * <p>PO-Entscheidung 2026-09-17 für „Verfügbarkeit & Vertrieb": im Tagesraster stehen die
 * Beträge nebeneinander statt untereinander, und die glatte Null trägt dort keine Information.
 * Gerundet wird nichts — „225,50 €" bleibt vollständig. Für Listen, Belege und Faktenbänder
 * gilt weiterhin `formatMoney`; der eigene Name hält die Ausnahme sichtbar.</p>
 */
export function formatNightlyRate(amount: number, currency = 'EUR'): string {
  // Entweder ganz ohne Cents oder mit beiden Stellen: „225,5 €" waere ein Preis, den es nicht gibt.
  const hasCents = Math.round(amount * 100) % 100 !== 0;
  return new Intl.NumberFormat(DE, {
    style: 'currency',
    currency,
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat(DE).format(value);
}

/** Deutsche Dezimalzahl mit fester Genauigkeit, etwa für Preisbeispiele ohne Währungssymbol. */
export function formatDecimal(value: number, fractionDigits = 2): string {
  return new Intl.NumberFormat(DE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** Kompakte Dateigroesse (B / KB / MB / GB) mit DE-Dezimalkomma. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1).replace('.', ',')} GB`;
}

export function formatDate(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const date = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(DE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function formatDateTime(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const date = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(DE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Zeitraum aus zwei ISO-Tagen für Bestätigungen: „18. – 19. Sep. 2026". Über Monats- oder
 * Jahresgrenzen trägt auch der Beginn Monat und Jahr, sonst läse sich 15. Sep. – 20. Nov. als
 * „15. – 20. Nov.".
 */
export function formatDateRange(from: string, to: string): string {
  const day = (iso: string) => new Date(`${iso}T00:00:00Z`);
  const full = new Intl.DateTimeFormat(DE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  if (from === to) return full.format(day(to));
  const start = day(from);
  const end = day(to);
  if (start.getUTCFullYear() !== end.getUTCFullYear()) {
    return `${full.format(start)} – ${full.format(end)}`;
  }
  if (start.getUTCMonth() !== end.getUTCMonth()) {
    const dayMonth = new Intl.DateTimeFormat(DE, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
    return `${dayMonth.format(start)} – ${full.format(end)}`;
  }
  return `${start.getUTCDate()}. – ${full.format(end)}`;
}

/**
 * Prozent-Anzeige fuer KPIs. Erwartet bereits-skalierten Wert (42 fuer 42 %, nicht 0.42).
 * Backend liefert OccupancyTodayPercent bereits in 0..100.
 */
export function formatPercent(value: number, fractionDigits = 0): string {
  return `${new Intl.NumberFormat(DE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)} %`;
}

export function formatRelative(input: string | Date): string {
  const date = typeof input === 'string' ? new Date(input) : input;
  const diff = Date.now() - date.getTime();
  const seconds = Math.round(diff / 1000);
  if (seconds < 60) return 'gerade eben';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `vor ${minutes} Min.`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `vor ${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 30) return `vor ${days} Tagen`;
  return formatDate(date);
}
